import React, {
  useState, useMemo, useEffect,
} from "react";
import {
  Table, Button, Empty, Checkbox, Space, Spin, Tooltip, Select,
} from "antd";
import {
  BarChartOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from "recharts";
import moment from "moment";
import { fetchCustomerSampleData } from "../../../actions/dashboard";
import {
  buildTestAggregatesForTableAndBarChart,
  buildTestCategoryDateFlatSeries,
  buildTestCategoryRollupForBarChart,
  buildTestDateActivityRollup,
  buildTestDateAggregatedTrendFlatSeries,
  pivotTestCategoryFlatToLineChartByMetrics,
  TEST_LINE_METRIC_LABELS,
} from "./Helper";
import CustomerSubmissionDetailModal, {
  SUBMISSION_DETAIL_MODAL_VARIANT_BY_CATEGORY,
  SUBMISSION_DETAIL_MODAL_VARIANT_BY_RESULT_STATUS,
  SUBMISSION_DETAIL_MODAL_VARIANT_BY_TEST,
} from "./CustomerSubmissionDetailModal";
import "./DashboardShared.css";
import "./Customers.css";
import "./TestMetrics.css";
import dashboardChartTheme from "./dashboardChartTheme";
import DashboardBrushTraveller from "./DashboardBrushTraveller";

const LINE_METRIC_KEYS = ["total_tests", "on_time_count", "delayed_count", "pending_count"];

const testMetricsBrushLabelGutter = dashboardChartTheme.layout.testMetricsBrushLabelGutter;

const TREND_NUMERIC_FIELDS = ["total_tests", "pending_count", "on_time_count", "delayed_count", "product_count", "environment_count"];
const ACTIVITY_NUMERIC_FIELDS = ["pending_count", "delayed_count", "on_time_count", "product_count", "environment_count"];

function getBucketKey(date, frequency) {
  const m = moment(date, "MM/DD/YYYY");
  if (!m.isValid()) return null;
  return frequency === "weekly"
    ? m.startOf("week").format("MM/DD/YYYY")
    : m.startOf("month").format("MM/YYYY");
}

function groupFlatSeriesByFrequency(flatRows, frequency) {
  if (frequency === "daily" || !flatRows.length) return flatRows;
  const categoryLabel = flatRows[0].category;
  const buckets = new Map();

  flatRows.forEach((row) => {
    const key = getBucketKey(row.date, frequency);
    if (!key) return;
    if (!buckets.has(key)) {
      buckets.set(key, {
        date: key,
        category: categoryLabel,
        total_tests: 0,
        pending_count: 0,
        on_time_count: 0,
        delayed_count: 0,
        product_count: 0,
        environment_count: 0,
      });
    }
    const bucket = buckets.get(key);
    TREND_NUMERIC_FIELDS.forEach((f) => { bucket[f] += row[f] ?? 0; });
  });

  if (!buckets.size) return [];
  const fmt = frequency === "weekly" ? "MM/DD/YYYY" : "MM/YYYY";
  const sorted = Array.from(buckets.values()).sort((a, b) => moment(a.date, fmt).valueOf() - moment(b.date, fmt).valueOf());
  const unit = frequency === "weekly" ? "week" : "month";
  const cursor = moment(sorted[0].date, fmt).clone();
  const last = moment(sorted[sorted.length - 1].date, fmt);
  const filled = [];

  while (cursor.isSameOrBefore(last, unit)) {
    const key = cursor.format(fmt);
    filled.push(buckets.get(key) ?? {
      date: key,
      category: categoryLabel,
      total_tests: 0,
      pending_count: 0,
      on_time_count: 0,
      delayed_count: 0,
      product_count: 0,
      environment_count: 0,
    });
    cursor.add(1, unit);
  }
  return filled;
}

function groupActivityRollupByFrequency(activityMap, frequency) {
  if (frequency === "daily" || !(activityMap instanceof Map) || !activityMap.size) return activityMap;
  const result = new Map();

  activityMap.forEach((counts, dailyDate) => {
    const key = getBucketKey(dailyDate, frequency);
    if (!key) return;
    if (!result.has(key)) {
      result.set(key, {
        pending_count: 0,
        delayed_count: 0,
        on_time_count: 0,
        product_count: 0,
        environment_count: 0,
      });
    }
    const bucket = result.get(key);
    ACTIVITY_NUMERIC_FIELDS.forEach((f) => { bucket[f] += counts[f] ?? 0; });
  });
  return result;
}

function testMetricsProductEnvTooltipTitle(record, type) {
  let product = record?.productCount?.total ?? "-";
  let env = record?.envCount?.total ?? "-";

  if (type) {
    product = record?.productCount[type] ?? "-";
    env = record?.envCount[type] ?? "-";
  }

  return (
    <div>
      <span>
        Product:
        {" "}
        {product}
      </span>
      <br />
      <span>
        Environmental:
        {" "}
        {env}
      </span>
    </div>
  );
}

const TestMetrics = ({
  customerId,
  customerName,
  dateRange,
  filterRetest,
  searchParams,
}) => {
  const [viewMode, setViewMode] = useState("table");
  const [testNameModalRow, setTestNameModalRow] = useState(null);
  const [sampleStyleCategoryModal, setSampleStyleCategoryModal] = useState(null);
  const [sampleRows, setSampleRows] = useState([]);
  const [testMetricsDataLoading, setTestMetricsDataLoading] = useState(false);
  const [chartVisibility, setChartVisibility] = useState({
    total: true,
    onTime: true,
    delayed: true,
    pending: true,
  });
  const [lineMetricVisibility, setLineMetricVisibility] = useState({
    total_tests: true,
    on_time_count: true,
    delayed_count: true,
    pending_count: true,
  });
  const [trendFrequency, setTrendFrequency] = useState("daily");

  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return null;
    return `${((value / total) * 100).toFixed(2)}%`;
  };

  const formatValueWithPercentage = (value, total) => {
    const percentage = calculatePercentage(value, total);
    if (!percentage) return value;
    return `${value} (${percentage})`;
  };

  const parseDateRange = (dateRangeStr) => {
    if (!dateRangeStr || dateRangeStr === "") {
      return null;
    }
    const [startStr, endStr] = dateRangeStr.split("-");
    if (!startStr || !endStr) {
      return null;
    }
    const start = moment(startStr, "MM/DD/YYYY");
    const end = moment(endStr, "MM/DD/YYYY");
    if (!start.isValid() || !end.isValid()) {
      return null;
    }
    return { start, end };
  };

  const dateRangeObj = useMemo(() => parseDateRange(dateRange), [dateRange]);

  useEffect(() => {
    if (!customerId || !dateRangeObj) {
      setSampleRows([]);
      return undefined;
    }

    const controller = new AbortController();
    let requestIsActive = true;

    const loadTestMetricsSampleData = async () => {
      setTestMetricsDataLoading(true);
      try {
        const sampleIdValue = searchParams.paramName === "sample_id" ? searchParams.value : "";
        const sampleTypeValue = searchParams.paramName === "sample_type" ? searchParams.value : "";
        const response = await fetchCustomerSampleData({
          customer_pk: customerId,
          date_range: dateRange,
          sample_id: sampleIdValue,
          sample_type: sampleTypeValue,
          retest: filterRetest,
        }, controller.signal);

        const { success, result, canceled } = response;

        if (!requestIsActive || canceled) return;

        if (success && Array.isArray(result)) {
          setSampleRows(result);
        } else if (!canceled) {
          setSampleRows([]);
          toast.error(response.message);
        }
      } finally {
        if (requestIsActive) {
          setTestMetricsDataLoading(false);
        }
      }
    };

    loadTestMetricsSampleData();

    return () => {
      requestIsActive = false;
      controller.abort();
    };
  }, [
    customerId,
    dateRange,
    dateRangeObj,
    filterRetest,
    searchParams.paramName,
    searchParams.value,
  ]);

  const mainTableData = useMemo(
    () => buildTestAggregatesForTableAndBarChart(sampleRows),
    [sampleRows],
  );

  const barChartCategoryData = useMemo(
    () => buildTestCategoryRollupForBarChart(mainTableData),
    [mainTableData],
  );

  const testCategoryFlatSeries = useMemo(
    () => buildTestCategoryDateFlatSeries(sampleRows),
    [sampleRows],
  );

  const trendLineFlatSeries = useMemo(
    () => buildTestDateAggregatedTrendFlatSeries(testCategoryFlatSeries),
    [testCategoryFlatSeries],
  );

  const trendLineFlatSeriesForFrequency = useMemo(
    () => groupFlatSeriesByFrequency(trendLineFlatSeries, trendFrequency),
    [trendLineFlatSeries, trendFrequency],
  );

  const testDateActivityRollup = useMemo(
    () => buildTestDateActivityRollup(sampleRows),
    [sampleRows],
  );

  const testDateActivityRollupForFrequency = useMemo(
    () => groupActivityRollupByFrequency(testDateActivityRollup, trendFrequency),
    [testDateActivityRollup, trendFrequency],
  );

  const activeLineMetrics = useMemo(
    () => LINE_METRIC_KEYS.filter((metricKey) => lineMetricVisibility[metricKey]),
    [lineMetricVisibility],
  );

  const { chartRows: trendChartRowsBase, lineDescriptors } = useMemo(() => {
    if (activeLineMetrics.length === 0) {
      return { chartRows: [], lineDescriptors: [] };
    }
    return pivotTestCategoryFlatToLineChartByMetrics(trendLineFlatSeriesForFrequency, activeLineMetrics);
  }, [trendLineFlatSeriesForFrequency, activeLineMetrics]);

  const totals = useMemo(() => {
    if (!mainTableData.length) {
      return {
        totalCount: 0, onTime: 0, delayed: 0, pending: 0,
      };
    }
    return mainTableData.reduce(
      (acc, row) => ({
        totalCount: acc.totalCount + row.totalCount,
        onTime: acc.onTime + row.onTime,
        delayed: acc.delayed + row.delayed,
        pending: acc.pending + row.pending,
      }),
      {
        totalCount: 0, onTime: 0, delayed: 0, pending: 0,
      },
    );
  }, [mainTableData]);

  const openSampleStyleCategoryModal = (record, resultStatus) => {
    setTestNameModalRow(null);
    setSampleStyleCategoryModal({
      testCategory: record.testCategory,
      testCategoryPkParam: record.test_category_pk,
      testNamePkParam: record.testName || "",
      testPkParam: record.test_pk,
      resultStatus,
    });
  };

  const openTestNameModal = (record) => {
    setSampleStyleCategoryModal(null);
    setTestNameModalRow(record);
  };

  const columns = [
    {
      title: "Test Category",
      dataIndex: "testCategory",
      key: "testCategory",
      render: (text, record) => (
        <button
          type="button"
          className="TestMetrics__ClickableCell"
          onClick={(clickEvent) => {
            clickEvent.stopPropagation();
            openSampleStyleCategoryModal(record);
          }}
        >
          {text || "—"}
        </button>
      ),
    },
    {
      title: "Test Name",
      dataIndex: "testName",
      key: "testName",
      render: (text, record) => (
        <button
          type="button"
          className="TestMetrics__ClickableCell"
          onClick={(clickEvent) => {
            clickEvent.stopPropagation();
            openTestNameModal(record);
          }}
        >
          <span>{text || "—"}</span>
        </button>
      ),
    },
    {
      title: "Total Count",
      dataIndex: "totalCount",
      key: "totalCount",
      align: "right",
      render: (text, record) => (
        <Tooltip placement="top" title={testMetricsProductEnvTooltipTitle(record)}>
          <span className="TestMetrics__MetricCellTooltipWrap">{text ?? "—"}</span>
        </Tooltip>
      ),
    },
    {
      title: "On Time",
      dataIndex: "onTime",
      key: "onTime",
      align: "right",
      render: (text, record) => {
        const data = formatValueWithPercentage(record.onTime, record.totalCount);
        return (
          <Tooltip title={testMetricsProductEnvTooltipTitle(record, "onTime")}>
            <button
              type="button"
              className="TestMetrics__ClickableCell TestMetrics__MetricCellTooltipWrap"
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                openSampleStyleCategoryModal(record, "onTimeRows");
              }}
            >
              {data || "—"}
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: "Delayed",
      dataIndex: "delayed",
      key: "delayed",
      align: "right",
      render: (text, record) => {
        const data = formatValueWithPercentage(record.delayed, record.totalCount);
        return (
          <Tooltip placement="top" title={testMetricsProductEnvTooltipTitle(record, "delayed")}>
            <button
              type="button"
              className="TestMetrics__ClickableCell TestMetrics__MetricCellTooltipWrap"
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                openSampleStyleCategoryModal(record, "delayedRows");
              }}
            >
              {data || "—"}
            </button>
          </Tooltip>
        );
      },
    },
    {
      title: "Pending",
      dataIndex: "pending",
      key: "pending",
      align: "right",
      render: (text, record) => {
        const data = formatValueWithPercentage(record.pending, record.totalCount);
        return (
          <Tooltip placement="top" title={testMetricsProductEnvTooltipTitle(record, "pending")}>
            <button
              type="button"
              className="TestMetrics__ClickableCell TestMetrics__MetricCellTooltipWrap"
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();
                openSampleStyleCategoryModal(record, "pendingRows");
              }}
            >
              {data || "—"}
            </button>
          </Tooltip>
        );
      },
    },
  ];

  const summary = () => (
    <Table.Summary fixed="bottom">
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={2}>
          <strong>Total</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={1} align="right">
          <strong>{totals.totalCount}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={2} align="right">
          <strong>{formatValueWithPercentage(totals.onTime, totals.totalCount)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
          <strong>{formatValueWithPercentage(totals.delayed, totals.totalCount)}</strong>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          <strong>{formatValueWithPercentage(totals.pending, totals.totalCount)}</strong>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );

  const toggleView = () => {
    setViewMode(viewMode === "table" ? "analytics" : "table");
  };

  const handleChartVisibilityChange = (key, checked) => {
    setChartVisibility((prev) => ({ ...prev, [key]: checked }));
  };

  const handleLineMetricVisibilityChange = (metricKey, checked) => {
    setLineMetricVisibility((previous) => {
      const next = { ...previous, [metricKey]: checked };
      const anyLineMetricOn = LINE_METRIC_KEYS.some((key) => next[key]);
      if (!anyLineMetricOn) {
        return previous;
      }
      return next;
    });
  };

  const CustomBarChartTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const tooltipCategoryLabel = data.testCategory || "—";

      return (
        <div className="TestMetrics__CustomTooltip">
          <p className="TestMetrics__CustomTooltip__Title">{tooltipCategoryLabel}</p>
          {payload.map((entry) => (
            <p
              key={entry.dataKey}
              className="TestMetrics__CustomTooltip__Item"
              style={{ color: entry.color }}
            >
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleCloseTestNameModal = () => {
    setTestNameModalRow(null);
  };

  const handleCloseSampleStyleCategoryModal = () => {
    setSampleStyleCategoryModal(null);
  };

  const categoryLineColorPalette = [
    dashboardChartTheme.testMetricsSeries.total,
    dashboardChartTheme.testMetricsSeries.onTime,
    dashboardChartTheme.testMetricsSeries.delayed,
    dashboardChartTheme.testMetricsSeries.pending,
    dashboardChartTheme.sampleSeries.product,
    dashboardChartTheme.sampleSeries.environment,
    "#8B6F47",
    "#4A7C8F",
  ];

  const trendLineStrokeByMetricField = {
    total_tests: dashboardChartTheme.testMetricsSeries.total,
    on_time_count: dashboardChartTheme.testMetricsSeries.onTime,
    delayed_count: dashboardChartTheme.testMetricsSeries.delayed,
    pending_count: dashboardChartTheme.testMetricsSeries.pending,
  };

  const trendTooltipValueByMetricField = {
    total_tests: (rollup) => rollup.pending_count + rollup.delayed_count + rollup.on_time_count,
    on_time_count: (rollup) => rollup.on_time_count,
    delayed_count: (rollup) => rollup.delayed_count,
    pending_count: (rollup) => rollup.pending_count,
  };

  const TestCategoryTrendTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const axisDate = payload[0]?.payload?.date ?? label ?? "";
    const rollup = testDateActivityRollupForFrequency.get(axisDate);
    if (!rollup) {
      return (
        <div className="TestMetrics__CustomTooltip">
          <p className="TestMetrics__CustomTooltip__Title">{axisDate}</p>
          <p className="TestMetrics__CustomTooltip__Item">No data for this date.</p>
        </div>
      );
    }
    const visibleMetricKeys = LINE_METRIC_KEYS.filter((metricKey) => lineMetricVisibility[metricKey]);
    return (
      <div className="TestMetrics__CustomTooltip">
        <p className="TestMetrics__CustomTooltip__Title">{axisDate}</p>
        <p className="TestMetrics__CustomTooltip__Item">All tests</p>
        {visibleMetricKeys.map((metricKey) => {
          const valueGetter = trendTooltipValueByMetricField[metricKey];
          const displayLabel = TEST_LINE_METRIC_LABELS[metricKey] ?? metricKey;
          const displayValue = valueGetter ? valueGetter(rollup) : 0;
          return (
            <p
              key={metricKey}
              className="TestMetrics__CustomTooltip__Item"
              style={{ color: trendLineStrokeByMetricField[metricKey] }}
            >
              {`${displayLabel}: ${displayValue}`}
            </p>
          );
        })}
      </div>
    );
  };

  const formatDateRangeDisplay = () => {
    if (!dateRangeObj) return "";
    const { start, end } = dateRangeObj;
    return `${start.format("MM/DD/YYYY")} - ${end.format("MM/DD/YYYY")}`;
  };

  if (!dateRange || dateRange === "" || !dateRangeObj) {
    return (
      <div className="TestMetrics">
        <div className="TestMetrics__EmptyState">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a date range above to view service level metrics"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="TestMetrics">
      <Spin spinning={testMetricsDataLoading}>
        <div className="TestMetrics__Header">
          <Button
            type="primary"
            icon={viewMode === "table" ? <BarChartOutlined /> : <TableOutlined />}
            onClick={toggleView}
          >
            {viewMode === "table" ? "Analytics View" : "Table View"}
          </Button>
        </div>

        {viewMode === "table" ? (
          <div className="TestMetrics__TableSection">
            <Table
              dataSource={mainTableData}
              columns={columns}
              rowKey="key"
              pagination={false}
              bordered
              summary={summary}
            />
          </div>
        ) : (
          <div className="TestMetrics__Analytics">
            {/* <div className="TestMetrics__Section">
              <h3 className="TestMetrics__SectionTitle">Test Metrics Summary</h3>
              <div className="Dashboard__StatsContainer TestMetrics__StatsContainer">
                <div className="Dashboard__StatsCard">
                  <div className="Dashboard__StatsCard__Header">
                    <span className="Dashboard__StatsCard__Type">Total</span>
                  </div>
                  <div className="Dashboard__StatsCard__Samples">{totals.totalCount}</div>
                </div>
                <div className="Dashboard__StatsCard">
                  <div className="Dashboard__StatsCard__Header">
                    <span className="Dashboard__StatsCard__Type">On Time</span>
                  </div>
                  <div className="Dashboard__StatsCard__Samples">{totals.onTime}</div>
                </div>
                <div className="Dashboard__StatsCard">
                  <div className="Dashboard__StatsCard__Header">
                    <span className="Dashboard__StatsCard__Type">Delayed</span>
                  </div>
                  <div className="Dashboard__StatsCard__Samples">{totals.delayed}</div>
                </div>
                <div className="Dashboard__StatsCard">
                  <div className="Dashboard__StatsCard__Header">
                    <span className="Dashboard__StatsCard__Type">Pending</span>
                  </div>
                  <div className="Dashboard__StatsCard__Samples">{totals.pending}</div>
                </div>
              </div>
            </div> */}

            <div className="TestMetrics__ChartCard">
              <div className="TestMetrics__ChartHeader">
                <h3>Trend Analysis</h3>
                <div className="TestMetrics__FrequencyDropdown">
                  <Select
                    value={trendFrequency}
                    onChange={(value) => setTrendFrequency(value)}
                    options={[
                      { label: "Daily", value: "daily" },
                      { label: "Weekly", value: "weekly" },
                      { label: "Monthly", value: "monthly" },
                    ]}
                  />
                </div>
                <div className="TestMetrics__ChartControls">
                  <Space wrap>
                    <Checkbox
                      checked={lineMetricVisibility.total_tests}
                      onChange={(event) => handleLineMetricVisibilityChange("total_tests", event.target.checked)}
                    >
                      Total
                    </Checkbox>
                    <Checkbox
                      checked={lineMetricVisibility.on_time_count}
                      onChange={(event) => handleLineMetricVisibilityChange("on_time_count", event.target.checked)}
                    >
                      On Time
                    </Checkbox>
                    <Checkbox
                      checked={lineMetricVisibility.delayed_count}
                      onChange={(event) => handleLineMetricVisibilityChange("delayed_count", event.target.checked)}
                    >
                      Delayed
                    </Checkbox>
                    <Checkbox
                      checked={lineMetricVisibility.pending_count}
                      onChange={(event) => handleLineMetricVisibilityChange("pending_count", event.target.checked)}
                    >
                      Pending
                    </Checkbox>
                  </Space>
                </div>
              </div>
              {trendChartRowsBase && trendChartRowsBase.length > 0 ? (
                <ResponsiveContainer width="100%" height={dashboardChartTheme.layout.lineWithBrush.responsiveHeight}>
                  <LineChart
                    data={trendChartRowsBase}
                    margin={{
                      top: dashboardChartTheme.layout.lineWithBrush.marginTop,
                      right: dashboardChartTheme.layout.lineWithBrush.marginRight + testMetricsBrushLabelGutter,
                      left: dashboardChartTheme.layout.lineWithBrush.marginLeft + testMetricsBrushLabelGutter,
                      bottom: dashboardChartTheme.layout.lineWithBrush.marginBottom,
                    }}
                  >
                    <CartesianGrid
                      stroke={dashboardChartTheme.gridStroke}
                      strokeDasharray="3 3"
                    />
                    <XAxis
                      dataKey="date"
                      angle={-45}
                      textAnchor="end"
                      height={dashboardChartTheme.layout.lineWithBrush.xAxisHeight}
                      tickMargin={dashboardChartTheme.layout.lineWithBrush.xAxisTickMargin}
                      // interval={Math.max(0, Math.floor(trendChartRowsBase.length / 10))}
                      minTickGap={16}
                    />
                    <YAxis />
                    <RechartsTooltip content={<TestCategoryTrendTooltip />} />
                    <Legend />
                    {lineDescriptors.map((descriptor) => (
                      <Line
                        key={descriptor.dataKey}
                        type={dashboardChartTheme.line.curveType}
                        dataKey={descriptor.dataKey}
                        stroke={
                          trendLineStrokeByMetricField[descriptor.metricField]
                          ?? categoryLineColorPalette[descriptor.categoryIndex % categoryLineColorPalette.length]
                        }
                        strokeWidth={dashboardChartTheme.line.strokeWidth}
                        strokeLinecap={dashboardChartTheme.line.strokeLinecap}
                        strokeLinejoin={dashboardChartTheme.line.strokeLinejoin}
                        name={descriptor.legendName}
                        connectNulls
                      />
                    ))}
                    <Brush
                      dataKey="date"
                      stroke={dashboardChartTheme.brush.stroke}
                      fill={dashboardChartTheme.brush.fill}
                      height={dashboardChartTheme.brush.height}
                      travellerWidth={dashboardChartTheme.brush.travellerWidth}
                      traveller={DashboardBrushTraveller}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )}
            </div>

            <div className="TestMetrics__ChartCard">
              <div className="TestMetrics__ChartHeader">
                <h3>Test Analysis</h3>
                <div className="TestMetrics__ChartControls">
                  <Space>
                    <Checkbox
                      checked={chartVisibility.total}
                      onChange={(e) => handleChartVisibilityChange("total", e.target.checked)}
                    >
                      Total
                    </Checkbox>
                    <Checkbox
                      checked={chartVisibility.onTime}
                      onChange={(e) => handleChartVisibilityChange("onTime", e.target.checked)}
                    >
                      On Time
                    </Checkbox>
                    <Checkbox
                      checked={chartVisibility.delayed}
                      onChange={(e) => handleChartVisibilityChange("delayed", e.target.checked)}
                    >
                      Delayed
                    </Checkbox>
                    <Checkbox
                      checked={chartVisibility.pending}
                      onChange={(e) => handleChartVisibilityChange("pending", e.target.checked)}
                    >
                      Pending
                    </Checkbox>
                  </Space>
                </div>
              </div>

              {barChartCategoryData && barChartCategoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height={dashboardChartTheme.layout.barWithBrush.responsiveHeight}>
                  <BarChart
                    data={barChartCategoryData}
                    margin={{
                      top: dashboardChartTheme.layout.barWithBrush.marginTop,
                      right: dashboardChartTheme.layout.barWithBrush.marginRight + testMetricsBrushLabelGutter,
                      left: dashboardChartTheme.layout.barWithBrush.marginLeft + testMetricsBrushLabelGutter,
                      bottom: dashboardChartTheme.layout.barWithBrush.marginBottom,
                    }}
                  >
                    <CartesianGrid
                      stroke={dashboardChartTheme.gridStroke}
                      strokeDasharray="3 3"
                    />
                    <XAxis
                      dataKey="testCategory"
                      angle={-45}
                      textAnchor="end"
                      height={dashboardChartTheme.layout.barWithBrush.xAxisHeight}
                      tickMargin={dashboardChartTheme.layout.barWithBrush.xAxisTickMargin}
                      minTickGap={20}
                    />
                    <YAxis />
                    <RechartsTooltip content={<CustomBarChartTooltip />} />
                    <Legend />
                    {chartVisibility.total && (
                      <Bar
                        dataKey="totalCount"
                        fill={dashboardChartTheme.testMetricsSeries.total}
                        name="Total"
                      />
                    )}
                    {chartVisibility.onTime && (
                      <Bar
                        dataKey="onTime"
                        fill={dashboardChartTheme.testMetricsSeries.onTime}
                        name="On Time"
                      />
                    )}
                    {chartVisibility.delayed && (
                      <Bar
                        dataKey="delayed"
                        fill={dashboardChartTheme.testMetricsSeries.delayed}
                        name="Delayed"
                      />
                    )}
                    {chartVisibility.pending && (
                      <Bar
                        dataKey="pending"
                        fill={dashboardChartTheme.testMetricsSeries.pending}
                        name="Pending"
                      />
                    )}
                    <Brush
                      dataKey="testCategory"
                      stroke={dashboardChartTheme.brush.stroke}
                      fill={dashboardChartTheme.brush.fill}
                      height={dashboardChartTheme.brush.height}
                      travellerWidth={dashboardChartTheme.brush.travellerWidth}
                      traveller={DashboardBrushTraveller}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div>
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </div>
              )}
            </div>
          </div>
        )}
      </Spin>

      {sampleStyleCategoryModal && (
        <CustomerSubmissionDetailModal
          variant={
            sampleStyleCategoryModal.resultStatus
              ? SUBMISSION_DETAIL_MODAL_VARIANT_BY_RESULT_STATUS
              : SUBMISSION_DETAIL_MODAL_VARIANT_BY_CATEGORY
          }
          testCategoryLabel={sampleStyleCategoryModal.testCategory}
          customerName={customerName}
          dateRangeDisplay={formatDateRangeDisplay()}
          onClose={handleCloseSampleStyleCategoryModal}
          customerPrimaryKey={customerId}
          dateRangeForApi={dateRange}
          testCategoryPkParam={sampleStyleCategoryModal.testCategoryPkParam}
          testNamePkParam={sampleStyleCategoryModal.testNamePkParam}
          testPkParam={sampleStyleCategoryModal.testPkParam}
          resultStatus={sampleStyleCategoryModal.resultStatus}
          sampleRows={sampleRows}
          isRetest={filterRetest}
          searchParams={searchParams}
        />
      )}
      {testNameModalRow && (
        <CustomerSubmissionDetailModal
          variant={SUBMISSION_DETAIL_MODAL_VARIANT_BY_TEST}
          testCategoryLabel={testNameModalRow.testCategory}
          testNameLabel={testNameModalRow.testName}
          customerName={customerName}
          dateRangeDisplay={formatDateRangeDisplay()}
          onClose={handleCloseTestNameModal}
          customerPrimaryKey={customerId}
          dateRangeForApi={dateRange}
          testCategoryPkParam={
            testNameModalRow.test_category_pk != null && testNameModalRow.test_category_pk !== ""
              ? String(testNameModalRow.test_category_pk)
              : ""
          }
          testNamePkParam={
            testNameModalRow.test_pk != null && testNameModalRow.test_pk !== ""
              ? String(testNameModalRow.test_pk)
              : ""
          }
          testPkParam={testNameModalRow.test_pk}
          isRetest={filterRetest}
          searchParams={searchParams}
        />
      )}
    </div>
  );
};

export default TestMetrics;
